import { useState, useEffect, useCallback, useMemo } from 'react';

// Pin one line, or the lines of one save, to the top of a list until the
// user acts (#1933; a set since #2263, when a split saves several at once).
//
// Presentation only. The caller's sorted array stays the source of truth
// (#1845): the caller renders the pinned lines first, in pin order, instead
// of at their sorted positions, never twice, and nothing here reorders data.
// The pin ends when the caller says so (the next add replaces it; a filter
// change or reload calls release) and, derived here, whenever every pinned
// line has left the rendered set (reconciled under the Unreconciled filter,
// deleted, moved). A line that leaves on its own is dropped from the set, so
// it does not re-pin when a filter flips back and brings it into view again.
// Nothing persists across reload.
//
// `lines` is the list as rendered (after any filter), each item carrying a
// `lineId`. `pinnedIndexes` are the pinned lines' indexes in that list, in
// pin order; `pinnedIndex` is the first of them, or -1, for callers that pin
// one line.
export const usePinnedLine = (lines) => {
  const [pinnedIds, setPinnedIds] = useState([]);

  const pinnedIndexes = useMemo(
    () => pinnedIds.map((id) => lines.findIndex((l) => l.lineId === id)).filter((idx) => idx !== -1),
    [lines, pinnedIds]
  );

  // A line left the rendered set: forget it, do not wait for the next add.
  useEffect(() => {
    if (pinnedIds.length === 0 || pinnedIndexes.length === pinnedIds.length) return;
    const present = new Set(pinnedIndexes.map((idx) => lines[idx].lineId));
    setPinnedIds(pinnedIds.filter((id) => present.has(id)));
  }, [lines, pinnedIds, pinnedIndexes]);

  const pin = useCallback((lineIds) => {
    const ids = Array.isArray(lineIds) ? lineIds : [lineIds];
    setPinnedIds(ids.filter((id) => id != null));
  }, []);
  const release = useCallback(() => setPinnedIds([]), []);

  const pinnedIndex = pinnedIndexes.length > 0 ? pinnedIndexes[0] : -1;
  return {
    pinnedLineId: pinnedIndex === -1 ? null : lines[pinnedIndex].lineId,
    pinnedIndex,
    pinnedIndexes,
    pin,
    release,
  };
};
